import { View, type LayoutChangeEvent } from "@-/com.npmjs.react-native";
import { useLearnCardSurface } from "#learn-card-surface-context";
import { useCallback, useEffect, useRef } from "react";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";

/**
 * Reports the surface it sits in as ready.
 *
 * Placed by a face INSIDE its Suspense-resolved content, so it only ever mounts once the
 * essential data has rendered — and it waits for its own first layout, which proves the surface
 * has been measured by the layout system rather than merely committed by React. A zero-size
 * view, so it changes nothing about the face.
 */
export function LearnFlashcardSurfaceReadyMarker() {
  const { reportReady } = useLearnCardSurface();
  const isNavigationFocused = useIsNavigationFocused();
  const hasReportedRef = useRef(false);
  const report = useCallback(() => {
    if (hasReportedRef.current) {
      return;
    }
    hasReportedRef.current = true;
    reportReady();
  }, [reportReady]);

  const handleLayout = useCallback(
    (_event: LayoutChangeEvent) => {
      report();
    },
    [report],
  );

  // A detached native tab does not deliver layout events. Its Suspense-resolved content must
  // still unlock the next preload. Visible native surfaces retain the stronger layout gate;
  // off-tab surfaces use the completed content commit, as web's zero-size marker already does.
  useEffect(() => {
    if (!isNavigationFocused || typeof document !== "undefined") {
      report();
    }
  }, [isNavigationFocused, report]);

  return (
    <View
      className="size-0"
      collapsable={false}
      onLayout={handleLayout}
      testID="learn-card-surface-ready"
    />
  );
}
